import { useState } from 'preact/hooks';
import { Crosshair, Dices, FileText, FlaskConical, Gamepad2, GraduationCap, Hand, Heart, LogIn, LogOut, Maximize, Monitor, ScrollText, Settings as SettingsIcon, ShieldCheck, Trash, UserRound, Users, Volume2, VolumeX } from 'lucide-preact';
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { LOOK_MAX, LOOK_MIN } from '../../game/input/look';
import { actionCodes, BIND_ACTIONS, codeLabel, DEFAULT_BINDINGS, mouseCode, rebind, resetBinding, resolveBindings, type BindAction } from '../../game/input/Input';
import { audio } from '../../game/audio/Audio';
import { canFullscreen, enterFullscreen } from '../fullscreen';
import { copyText } from '../platform';
import { autoQuality, settings, updateSettings, type ClientSettings } from '../settings';
import { devLabEnabled, rename, runTestLab } from '../state/actions';
import { nameProblem } from '../state/names';
import { useNameRoller } from './Onboarding';
import { RENAME_PRICE } from '@vm/content';
import { go, replace, route } from '../state/nav';
import { startTutorial } from '../state/play';
import { deleteAccount, login, logout, registerEmail } from '../state/session';
import { mutedName, muteUser } from '../state/socialActions';
import { isOffline, me, mutedUsers, restricted, toast } from '../state/store';
import { AsyncButton, confirmDialog, Field, OnlineOnly, Price, Screen, Segmented, Slider, Toggle } from '../ui/kit';
import { ForgotPasswordLink } from './PasswordReset';

type Section = 'controls' | 'graphics' | 'audio' | 'crosshair' | 'touch' | 'social' | 'account' | 'about' | 'lab';

const SECTIONS: { id: Section; label: string; icon: ComponentChildren }[] = [
  { id: 'controls', label: 'Controls', icon: <Gamepad2 size={20} /> },
  { id: 'graphics', label: 'Graphics', icon: <Monitor size={20} /> },
  { id: 'audio', label: 'Audio', icon: <Volume2 size={20} /> },
  { id: 'crosshair', label: 'Crosshair', icon: <Crosshair size={20} /> },
  { id: 'touch', label: 'Touch', icon: <Hand size={20} /> },
  { id: 'social', label: 'Social', icon: <Users size={20} /> },
  { id: 'account', label: 'Account', icon: <UserRound size={20} /> },
  { id: 'about', label: 'About', icon: <FileText size={20} /> },
];

const LAB_SECTION = { id: 'lab' as const, label: 'Test Lab', icon: <FlaskConical size={20} /> };

const set = (patch: Partial<ClientSettings>): void => updateSettings(patch);
const pct = (v: number): string => `${Math.round(v * 100)}%`;
const fmt = (v: number): string => v.toFixed(2);

function Keys(props: { codes: string[] }) {
  return (
    <span class="keys">
      {props.codes.map((c) => (
        <kbd key={c}>{codeLabel(c)}</kbd>
      ))}
    </span>
  );
}

/** The default code(s) of an action, for "is this changed?" and per-action reset. */
function defaultCodes(action: BindAction): string[] {
  return actionCodes(DEFAULT_BINDINGS, action);
}

function Controls() {
  const s = settings.value;
  const b = resolveBindings(s.keybinds);
  const [capturing, setCapturing] = useState<BindAction | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!capturing) return;
    const finish = (code: string | null): void => {
      if (code) {
        const r = rebind(s.keybinds, capturing, code);
        set({ keybinds: r.overrides });
        const other = r.swapped ? BIND_ACTIONS.find((a) => a.action === r.swapped)?.label : null;
        setNotice(other ? `${codeLabel(code)} was used by "${other}", so the two swapped.` : null);
      }
      setCapturing(null);
    };
    const onKey = (e: KeyboardEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      finish(e.code === 'Escape' ? null : e.code);
    };
    // A mouse click can be a binding too (Shoot / Aim and anything else), so a click while capturing is captured, not used as a click.
    const onMouse = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      finish(mouseCode(e.button));
    };
    const onContext = (e: Event): void => e.preventDefault();
    window.addEventListener('keydown', onKey, { capture: true });
    window.addEventListener('mousedown', onMouse, { capture: true });
    window.addEventListener('contextmenu', onContext, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true });
      window.removeEventListener('mousedown', onMouse, { capture: true });
      window.removeEventListener('contextmenu', onContext, { capture: true });
    };
  }, [capturing, s.keybinds]);
  const custom = Object.keys(s.keybinds ?? {}).length > 0;
  const changed = (action: BindAction): boolean => {
    const now = actionCodes(b, action);
    const def = defaultCodes(action);
    return now.length !== def.length || now.some((c, i) => c !== def[i]);
  };
  const resetOne = (action: BindAction): void => {
    const r = resetBinding(s.keybinds, action);
    set({ keybinds: r.overrides });
    const other = r.swapped ? BIND_ACTIONS.find((a) => a.action === r.swapped)?.label : null;
    setNotice(other ? `"${other}" had that key, so the two swapped.` : null);
  };
  const lookChanged = s.lookX !== 1 || s.lookY !== 1 || s.adsX !== 1 || s.adsY !== 1;
  return (
    <>
      <div class="set-group">
        <Slider label="Horizontal look" hint="Left / right turning speed." value={s.lookX} min={LOOK_MIN} max={LOOK_MAX} step={0.05} format={fmt} onChange={(v) => set({ lookX: v })} />
        <Slider label="Vertical look" hint="Up / down turning speed." value={s.lookY} min={LOOK_MIN} max={LOOK_MAX} step={0.05} format={fmt} onChange={(v) => set({ lookY: v })} />
        <Toggle label="Invert look" hint="Moving the mouse up looks down." checked={s.invertY} onChange={(v) => set({ invertY: v })} />
        <Toggle label="Raw mouse input" hint="Ignores your computer's mouse acceleration so the same swipe always turns the same amount." checked={s.rawMouse} onChange={(v) => set({ rawMouse: v })} />
      </div>
      <h3 class="set-title">Aiming down sights</h3>
      <p class="set-hint">Zoomed sights already turn slower so the picture moves the same speed on screen. These are on top of that: the same number as above feels the same when zoomed in.</p>
      <div class="set-group">
        <Slider label="Scoped horizontal look" hint="Left / right turning speed while aiming." value={s.adsX} min={LOOK_MIN} max={LOOK_MAX} step={0.05} format={fmt} onChange={(v) => set({ adsX: v })} />
        <Slider label="Scoped vertical look" hint="Up / down turning speed while aiming." value={s.adsY} min={LOOK_MIN} max={LOOK_MAX} step={0.05} format={fmt} onChange={(v) => set({ adsY: v })} />
        {lookChanged && (
          <button type="button" class="btn small" onClick={() => set({ lookX: 1, lookY: 1, adsX: 1, adsY: 1 })}>
            Reset look speeds to 1.00
          </button>
        )}
      </div>
      <h3 class="set-title">Keyboard & mouse</h3>
      <p class="set-hint">Click a control, then press the new key or mouse button (Esc cancels). If that key is already used, the two swap.</p>
      {notice && <p class="set-hint set-notice">{notice}</p>}
      <div class="keymap">
        {BIND_ACTIONS.map(({ action, label }) => (
          <div key={action} class="keymap-cell">
            <button type="button" class={`keymap-row rebind ${capturing === action ? 'capturing' : ''}`} onClick={() => setCapturing(capturing === action ? null : action)}>
              <span>{label}</span>
              {capturing === action ? (
                <span class="keys">
                  <kbd>Press a key or click…</kbd>
                </span>
              ) : (
                <Keys codes={actionCodes(b, action).length ? actionCodes(b, action) : ['—']} />
              )}
            </button>
            {changed(action) && capturing !== action && (
              <button type="button" class="keymap-reset" title={`Reset "${label}" to ${defaultCodes(action).map(codeLabel).join(' / ')}`} aria-label={`Reset ${label}`} onClick={() => resetOne(action)}>
                ↺
              </button>
            )}
          </div>
        ))}
        <div class="keymap-row">
          <span>Menu</span>
          <Keys codes={['Escape']} />
        </div>
      </div>
      {custom && (
        <button type="button" class="btn small" onClick={() => { set({ keybinds: {} }); setNotice(null); }}>
          Reset all keys to defaults
        </button>
      )}
    </>
  );
}

function Graphics() {
  const s = settings.value;
  return (
    <div class="set-group">
      <Field label="Graphics quality" hint={s.quality === 'auto' ? `Auto picks ${autoQuality()} on this device.` : 'Lower settings run smoother on older devices.'}>
        <Segmented
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]}
          value={s.quality}
          onChange={(v) => set({ quality: v })}
        />
      </Field>
      <Toggle label="Show FPS counter" checked={s.showFps} onChange={(v) => set({ showFps: v })} />
      <Toggle label="Reduce menu motion" hint="Fewer animations in the menus." checked={s.reduceMotion} onChange={(v) => set({ reduceMotion: v })} />
      {canFullscreen() && (
        <>
          <Toggle label="Full screen when playing" hint="Hides the browser's toolbars and turns the game sideways when a match starts." checked={s.autoFullscreen} onChange={(v) => set({ autoFullscreen: v })} />
          <button class="btn" onClick={() => void enterFullscreen()}>
            <Maximize size={18} /> Full screen now
          </button>
        </>
      )}
    </div>
  );
}

function AudioSection() {
  const s = settings.value;
  return (
    <div class="set-group">
      <Slider label="Master volume" value={s.masterVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => set({ masterVolume: v })} />
      <Slider label="Effects" value={s.sfxVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => set({ sfxVolume: v })} />
      <Slider label="Music" value={s.musicVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => set({ musicVolume: v })} />
      <button
        class="btn"
        onClick={() => {
          audio.unlock();
          audio.play('ui_buy');
        }}
      >
        <Volume2 size={18} /> Test sound
      </button>
    </div>
  );
}

const XH_COLORS = ['#ffffff', '#ffd24a', '#52d86a', '#4af0d0', '#4a9aff', '#ff6ab0', '#ff5a4a'];

function CrosshairSection() {
  const s = settings.value;
  return (
    <div class="set-group">
      <div class="xh-preview">
        <div class="crosshair" style={{ '--gap': '9px', '--xh': s.crosshairColor } as Record<string, string>}>
          {s.crosshairStyle === 'cross' && (
            <>
              <i class="xh xh-t" />
              <i class="xh xh-b" />
              <i class="xh xh-l" />
              <i class="xh xh-r" />
            </>
          )}
          {s.crosshairStyle === 'circle' && <i class="xh-circle" />}
          <i class="xh-dot" />
        </div>
      </div>
      <Field label="Style">
        <Segmented
          options={[
            { value: 'cross', label: 'Cross' },
            { value: 'dot', label: 'Dot' },
            { value: 'circle', label: 'Circle' },
          ]}
          value={s.crosshairStyle}
          onChange={(v) => set({ crosshairStyle: v })}
        />
      </Field>
      <Field label="Color">
        <div class="swatches">
          {XH_COLORS.map((c) => (
            <button key={c} class={`swatch ${s.crosshairColor === c ? 'is-on' : ''}`} style={{ background: c }} aria-label={`Crosshair color ${c}`} onClick={() => set({ crosshairColor: c })} />
          ))}
        </div>
      </Field>
    </div>
  );
}

function Touch() {
  const s = settings.value;
  return (
    <div class="set-group">
      <Field label="On-screen controls">
        <Segmented
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'on', label: 'Always' },
            { value: 'off', label: 'Off' },
          ]}
          value={s.touchControls}
          onChange={(v) => set({ touchControls: v })}
        />
      </Field>
      <Slider label="Button size" value={s.touchScale} min={0.7} max={1.4} step={0.05} format={pct} onChange={(v) => set({ touchScale: v })} />
      <Toggle label="Auto-fire" hint="Shoot automatically when your crosshair is on an enemy." checked={s.autoFire} onChange={(v) => set({ autoFire: v })} />
      <Toggle label="Aim assist" hint="Slightly slows aiming over enemies on touch screens and controllers." checked={s.aimAssist} onChange={(v) => set({ aimAssist: v })} />
    </div>
  );
}

function Social() {
  const s = settings.value;
  const muted = [...mutedUsers.value];
  return (
    <>
      <div class="set-group">
        <Toggle label="Show chat" hint="Turn off to hide the lobby chat completely." checked={s.chatEnabled} onChange={(v) => set({ chatEnabled: v })} />
      </div>
      <h3 class="set-title">Muted players</h3>
      {muted.length ? (
        <div class="set-list">
          {muted.map((id) => (
            <div key={id} class="set-list-row">
              <VolumeX size={18} />
              <span>{mutedName(id)}</span>
              <button class="btn sm push-right" onClick={() => muteUser(id, false)}>
                Unmute
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p class="muted small">Players you mute from chat appear here. Mutes only affect you.</p>
      )}
      <button class="btn" onClick={() => go('friends', { tab: 'blocked' })}>
        Blocked players
      </button>
    </>
  );
}

function NameChanger() {
  const m = me.value!;
  const kid = m.restricted;
  const [name, setName] = useState(m.name);
  const roll = useNameRoller();
  const problem = nameProblem(name);
  return (
    <div class="set-group">
      <Field
        label="Call sign"
        hint={m.renamed ? `Name changes cost ${RENAME_PRICE.price} ${RENAME_PRICE.currency}.` : 'Your first name change is free.'}
        error={name !== m.name ? problem : null}
      >
        <div class="ui-input-row">
          {kid ? (
            <div class="nameplate sm">{name}</div>
          ) : (
            <input class="ui-input" value={name} maxLength={16} onInput={(e) => setName((e.currentTarget as HTMLInputElement).value.replace(/\s/g, ''))} />
          )}
          <AsyncButton class="icon" aria-label="Suggest a name" onClick={async () => setName(await roll())}>
            <Dices size={20} />
          </AsyncButton>
          <AsyncButton
            class="primary"
            disabled={name === m.name || !!problem || isOffline.value}
            onClick={async () => {
              if (
                m.renamed &&
                !(await confirmDialog({
                  title: 'Change your name?',
                  body: (
                    <span>
                      This name change costs <Price price={RENAME_PRICE.price} currency={RENAME_PRICE.currency} />
                    </span>
                  ),
                  confirm: 'Change',
                }))
              )
                return;
              if (await rename(name)) toast(`You're now ${name}!`, { kind: 'success' });
            }}
          >
            Save
          </AsyncButton>
        </div>
      </Field>
    </div>
  );
}

function LinkEmail() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const valid = /.+@.+\..+/.test(email) && pw.length >= 8;
  return (
    <form class="set-group" onSubmit={(e) => e.preventDefault()}>
      <p class="muted small">Link an email to keep your marine safe and play on other devices.</p>
      <div class="form-grid">
        <Field label="Email">
          <input class="ui-input" type="email" autoComplete="email" value={email} onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Password" hint="At least 8 characters.">
          <input class="ui-input" type="password" autoComplete="new-password" value={pw} onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
        </Field>
      </div>
      <AsyncButton class="green" disabled={!valid} onClick={() => registerEmail(email, pw)}>
        <ShieldCheck size={18} /> Link email
      </AsyncButton>
    </form>
  );
}

function LoginOther() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const m = me.value!;
  const valid = /.+@.+\..+/.test(email) && pw.length >= 6;
  return (
    <form class="set-group" onSubmit={(e) => e.preventDefault()}>
      <p class="muted small">Already have a linked account from another device? Log in here.</p>
      <div class="form-grid">
        <Field label="Email">
          <input class="ui-input" type="email" autoComplete="email" value={email} onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Password">
          <input class="ui-input" type="password" autoComplete="current-password" value={pw} onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
        </Field>
      </div>
      <AsyncButton
        disabled={!valid}
        onClick={async () => {
          if (m.isGuest && !(await confirmDialog({ title: 'Switch accounts?', body: "This guest marine isn't linked to an email. If you switch, you can't come back to it.", confirm: 'Switch', danger: true })))
            return;
          await login(email, pw);
        }}
      >
        <LogIn size={18} /> Log in
      </AsyncButton>
      <ForgotPasswordLink email={email} />
    </form>
  );
}

function DeleteAccount() {
  const [typed, setTyped] = useState('');
  return (
    <div class="danger-zone">
      <h4>Delete account</h4>
      <p class="muted small">Permanently deletes your marine, progress, items and friends. Purchases can't be refunded after deletion.</p>
      <div class="ui-input-row">
        <input class="ui-input" placeholder='Type "DELETE" to confirm' value={typed} onInput={(e) => setTyped((e.currentTarget as HTMLInputElement).value)} />
        <AsyncButton
          class="danger"
          disabled={typed !== 'DELETE'}
          onClick={async () => {
            if (await confirmDialog({ title: 'Delete forever?', body: 'This cannot be undone.', confirm: 'Delete my account', danger: true })) await deleteAccount();
          }}
        >
          <Trash size={18} /> Delete
        </AsyncButton>
      </div>
    </div>
  );
}

function Account() {
  const m = me.value;
  if (!m) return null;
  return (
    <OnlineOnly feature="Account settings">
      <div class="account-card panel">
        <UserRound size={34} />
        <div>
          <b>{m.name}</b>
          <span class="muted small">{m.email ? `Linked to ${m.email}` : m.restricted ? 'Guest account (restricted mode)' : 'Guest account — not backed up'}</span>
        </div>
        {m.code && (
          <button
            class="friend-code sm"
            onClick={async () => {
              if (await copyText(m.code)) toast('Friend code copied', { kind: 'success' });
            }}
          >
            <span>Code</span>
            <b>{m.code}</b>
          </button>
        )}
      </div>
      <NameChanger />
      {!m.email &&
        (restricted.value ? (
          <p class="muted small">Backing up with an email isn't available on your account. Ask a parent if you need help.</p>
        ) : (
          <>
            <h3 class="set-title">Back up your account</h3>
            <LinkEmail />
          </>
        ))}
      <h3 class="set-title">Log in on this device</h3>
      <LoginOther />
      <h3 class="set-title">Session</h3>
      <div class="set-group">
        <AsyncButton
          onClick={async () => {
            const warn = m.isGuest && !m.email;
            if (
              await confirmDialog({
                title: 'Log out?',
                body: warn ? "You're playing as a guest. If you log out without linking an email, you could lose this marine." : 'You can log back in with your email any time.',
                confirm: 'Log out',
                danger: warn,
              })
            )
              await logout();
          }}
        >
          <LogOut size={18} /> Log out
        </AsyncButton>
      </div>
      <DeleteAccount />
    </OnlineOnly>
  );
}

function About() {
  return (
    <>
      <div class="about-links">
        <button class="btn" onClick={() => go('legal', { doc: 'privacy' })}>
          <ShieldCheck size={18} /> Privacy Policy
        </button>
        <button class="btn" onClick={() => go('legal', { doc: 'terms' })}>
          <ScrollText size={18} /> Terms of Service
        </button>
        <button class="btn" onClick={() => go('legal', { doc: 'parents' })}>
          <Users size={18} /> Parents' Guide
        </button>
        <button class="btn" onClick={() => go('legal', { doc: 'credits' })}>
          <Heart size={18} /> Credits
        </button>
      </div>
      <div class="set-group">
        <button class="btn green" onClick={() => startTutorial()}>
          <GraduationCap size={18} /> Replay Boot Camp
        </button>
      </div>
      <p class="muted small">Voxel Marines v{__APP_VERSION__}</p>
    </>
  );
}

/**
 * Test Lab: shortcuts for playtesting a development build. Shown only when the API says it is a development build (the
 * server also refuses these requests anywhere else), so players never see it.
 */
function TestLab() {
  const lvl = me.value?.progression.level ?? 1;
  const run = (req: Parameters<typeof runTestLab>[0], label: string, ask?: string) => async () => {
    if (ask && !(await confirmDialog({ title: label, body: ask, confirm: 'Do it' }))) return;
    await runTestLab(req, label);
  };
  return (
    <>
      <p class="muted">
        Only on test builds. These skip the grind so you can try every weapon, scope, tracer and look right away. Nothing here exists in the
        live game.
      </p>
      <h3 class="set-title">Unlock everything</h3>
      <div class="set-group lab-group">
        <AsyncButton class="primary" onClick={run({ unlockAll: true, level: 30, addCoins: 100000, addGems: 5000 }, 'Everything unlocked')}>
          Unlock everything (level 30 + coins + gems)
        </AsyncButton>
        <p class="muted small">Gives you every gun, scope, tracer, camo, skin and gadget to equip, sets you to level 30, and adds 100,000 coins and 5,000 gems.</p>
      </div>
      <h3 class="set-title">Pieces</h3>
      <div class="set-group lab-group">
        <AsyncButton onClick={run({ unlockAll: true }, 'Everything unlocked')}>Own every item (keep my level)</AsyncButton>
        <AsyncButton onClick={run({ level: 30 }, 'Level set to 30')}>Set level 30 {lvl !== 30 ? `(now ${lvl})` : ''}</AsyncButton>
        <AsyncButton onClick={run({ level: 100 }, 'Level set to 100')}>Set level 100</AsyncButton>
        <AsyncButton onClick={run({ addCoins: 50000 }, '+50,000 coins')}>+50,000 coins</AsyncButton>
        <AsyncButton onClick={run({ addGems: 2000 }, '+2,000 gems')}>+2,000 gems</AsyncButton>
        <AsyncButton onClick={run({ masteryKills: 1000 }, 'Every weapon at Diamond mastery')}>Every weapon: Diamond mastery</AsyncButton>
        <AsyncButton onClick={run({ masteryKills: 60 }, 'Every weapon near Bronze mastery')}>Every weapon: 60 kills (Bronze for most)</AsyncButton>
      </div>
      <p class="muted small">After unlocking, open the Armory, pick a weapon, and use its Sight, Tracer and Mastery rows. Scopes and tracers are also in the Store under Looks.</p>
    </>
  );
}

export function SettingsScreen() {
  const [lab, setLab] = useState(false);
  useEffect(() => {
    void devLabEnabled().then(setLab);
  }, []);
  const sections = lab ? [...SECTIONS, LAB_SECTION] : SECTIONS;
  const section = (route.value.params.tab as Section) || 'controls';
  return (
    <Screen title="Settings" icon={<SettingsIcon size={26} />}>
      <div class="settings">
        <nav class="settings-nav">
          {sections.map((s) => (
            <button
              key={s.id}
              class={`settings-nav-btn ${s.id === section ? 'is-on' : ''}`}
              onClick={() => {
                audio.play('ui_click');
                replace('settings', { tab: s.id });
              }}
            >
              {s.icon}
              <span>{s.label}</span>
            </button>
          ))}
        </nav>
        <div class="settings-body scr-scroll" key={section}>
          <h2 class="settings-title">{sections.find((s) => s.id === section)?.label}</h2>
          {section === 'controls' && <Controls />}
          {section === 'graphics' && <Graphics />}
          {section === 'audio' && <AudioSection />}
          {section === 'crosshair' && <CrosshairSection />}
          {section === 'touch' && <Touch />}
          {section === 'social' && <Social />}
          {section === 'account' && <Account />}
          {section === 'about' && <About />}
          {section === 'lab' && lab && <TestLab />}
        </div>
      </div>
    </Screen>
  );
}
